<template>
  <div class="modal" v-if="warningModalVisible" @click="hideModal">
    <div @click.stop class="modal-content">
      <div style="padding: 20px;">
        <div class="m-header">
          <h3 class="m-header-text">Save option</h3>
          <img class="close-modal-img" src="/images/close.png" @click="hideModal"/>
        </div>
        <div class="divider"></div>
         <div class="additional-text mt-15">
          By changing your input, certain dependent questions will have to be reset from our side and re-entered from your side in order to ensure a reliable computation of the case.
        </div> 
       <!-- <div class="additional-text mt-15">
          The following questions will be affected by your new selection: 
        </div>
        <ul v-for="(name, i) in dependentQuestionNames">
          <li class="additional-text">{{ i + 1}}. {{ name }}.</li>
        </ul> -->
        <br/>
        <div class="divider"></div>
        <div class="actions-btns">
          <simple-button style="max-width: 180px;" type="secondary" @click.native="hideModal">
            No, keep actual input
          </simple-button>
          <simple-button style="margin-left: 20px; max-width: 160px;" type="primary" @click.native="confirm">
            Save anyway
          </simple-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Prop, Vue, Component, Watch } from "vue-property-decorator";
import SimpleButton from './SimpleButton.vue';
import { SingleItemAnswer } from "@/entities/questionaire/answers/SingleItemAnswer";
import { MultipleItemsAnswer } from "@/entities/questionaire/answers/MultipleItemsAnswer";
import { BooleanAnswer } from "@/entities/questionaire/answers/BooleanAnswer";
import { Group } from "@/entities/questionaire/Group";
import { Property } from "@/entities/questionaire/Property";
import { Answer } from "@/entities/questionaire/answers/Answer";

@Component({
  name: 'SavingWarningModal',
  components: {
    SimpleButton,
  }
})
export default class SavingWarningModal extends Vue {
  @Prop()
  type!: string;

  @Prop()
  warningModalVisible!: boolean;

  @Prop()
  dependentQuestionNames!: string[];

  @Prop()
  questionId!: number;

  @Prop()
  variantId: number | undefined;

  @Prop()
  variantText: string | undefined;

  @Prop()
  variantsIds: number[] | undefined;

  @Prop()
  variantsTexts: string[] | undefined;

  @Prop()
  booleanAnswer: boolean | undefined;

  @Prop()
  localGroups!: Group[];

  @Prop()
  localQuestions!: Property[];

  // New props for range/yes-no questions
  @Prop()
  isRangeOrYesNo: boolean | undefined;

  @Prop()
  rangeSelections: { [key: number]: number } | undefined;

  @Prop()
  yesNoSelections: { [key: number]: boolean } | undefined;

  @Prop()
  totalScore: number | undefined;

  // New props for handling auto-answer cascades
  @Prop()
  answers!: Answer[];

  @Prop()
  property!: Property;

  hideModal() {
    this.$emit('update:warningModalVisible', false);
    this.$emit('update:dependentQuestionNames', []);
  }

  updateQuestionVisibilityInStore(questionId: number, isHidden: boolean) {
    console.log(`Syncing question ${questionId} hidden state: ${isHidden} to treatment store`);
    this.$store.commit('localQuestionsTreatment/setQuestionHidden', {
      id: questionId,
      isHidden
    });
  }

  // Helper method to calculate what an auto-answer question's score would be
  calculateAutoAnswerScore(autoAnswerQuestion: Property): number | null {
    if (!autoAnswerQuestion.autoAnswer || !autoAnswerQuestion.referenceQuestionId) {
      return null;
    }

    // Find the reference question's answer
    const referenceAnswer = this.answers.find(a => a.PropertyId === autoAnswerQuestion.referenceQuestionId);
    
    // If the reference is the current question being changed, use the new value
    if (autoAnswerQuestion.referenceQuestionId === this.questionId) {
      if (this.isRangeOrYesNo) {
        return this.totalScore || 0;
      } else {
        // For non-range questions, we can't calculate a score
        return null;
      }
    }
    
    // Otherwise use the existing answer
    if (referenceAnswer && 'answer' in referenceAnswer) {
      return (referenceAnswer as any).answer;
    }
    
    return null;
  }

  hideDepQuestions(question: Property, processedIds: Set<number> = new Set()) {
    // Prevent infinite recursion
    if (processedIds.has(question.id)) {
      return;
    }
    processedIds.add(question.id);

    const depQuestions = this.localQuestions.filter(q => q.questionConditionId === question.id);
    
    if (depQuestions.length > 0) {
      for (let i = 0; i < depQuestions.length; i++) {
        this.removeAnswer(depQuestions[i]);
        
        // Never show auto-answer questions
        if (depQuestions[i].autoAnswer === true) {
          depQuestions[i].isHidden = true;
          this.updateQuestionVisibilityInStore(depQuestions[i].id, true);
        } else {
          // For non-auto-answer questions, apply the parent's hidden state
          if (question.isHidden) {
            depQuestions[i].isHidden = true;
            this.updateQuestionVisibilityInStore(depQuestions[i].id, true);
          } else {
            depQuestions[i].isHidden = false;
            this.updateQuestionVisibilityInStore(depQuestions[i].id, false);
          }
        }
        
        this.hideDepQuestions(depQuestions[i], processedIds);
      }
    }
  }

  removeAnswer(question: Property) {
    if (question.type === 'singleItem') {
      this.$events.emit("answerChanged", new SingleItemAnswer(question.id, []));
    } else if (question.type === 'boolean') {
      this.$events.emit("answerChanged", new BooleanAnswer(question.id));
    } else {
      this.$events.emit("answerChanged", new MultipleItemsAnswer(question.id, []));
    }
  }

  // Helper to normalize text for pattern matching (same as in AppSingleItemAnswer)
  normalizeText(str: string): string {
    return String(str || "")
      .trim()
      .replace(/,/g, ".")
      .replace(/≥/g, ">=")
      .replace(/≤/g, "<=")
      .replace(/\s+/g, " ");
  }

  // Resolve auto-answer based on score (similar to handleAutoAnswer in AppSingleItemAnswer)
  resolveAutoAnswer(autoAnswerQuestion: Property, score: number): { variantId: number | null, variantText: string | null } {
    if (!autoAnswerQuestion.answer || !autoAnswerQuestion.answer.variants) {
      return { variantId: null, variantText: null };
    }

    // Find matching variant based on score pattern (Punkte logic)
    const matchingVariant = autoAnswerQuestion.answer.variants.find(v => {
      const text = this.normalizeText(v.text);
      
      // Pattern matching for different formats
      if (text.includes('-')) {
        // Range pattern like "6-9 Punkte"
        const match = text.match(/(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)/);
        if (match) {
          const min = parseFloat(match[1]);
          const max = parseFloat(match[2]);
          return score >= min && score <= max;
        }
      } else if (text.startsWith('>=')) {
        // Greater than or equal pattern
        const match = text.match(/>=\s*(\d+(?:\.\d+)?)/);
        if (match) {
          const value = parseFloat(match[1]);
          return score >= value;
        }
      } else if (text.startsWith('>')) {
        // Greater than pattern
        const match = text.match(/>\s*(\d+(?:\.\d+)?)/);
        if (match) {
          const value = parseFloat(match[1]);
          return score > value;
        }
      } else if (text.startsWith('<=')) {
        // Less than or equal pattern
        const match = text.match(/<=\s*(\d+(?:\.\d+)?)/);
        if (match) {
          const value = parseFloat(match[1]);
          return score <= value;
        }
      } else if (text.startsWith('<')) {
        // Less than pattern
        const match = text.match(/<\s*(\d+(?:\.\d+)?)/);
        if (match) {
          const value = parseFloat(match[1]);
          return score < value;
        }
      } else if (text.match(/^\d+(?:\.\d+)?\s*Punkte/)) {
        // Exact value pattern
        const match = text.match(/^(\d+(?:\.\d+)?)/);
        if (match) {
          const value = parseFloat(match[1]);
          return score === value;
        }
      }
      
      return false;
    });

    if (matchingVariant) {
      return { variantId: matchingVariant.id, variantText: matchingVariant.text };
    }

    return { variantId: null, variantText: null };
  }

// Process auto-answer questions and their dependents
processAutoAnswerCascade(autoAnswerQuestions: Property[]) {
    autoAnswerQuestions.forEach(autoQ => {
        // Calculate what the new auto-answer score would be
        const newScore = this.calculateAutoAnswerScore(autoQ);
        
        if (newScore !== null) {
            // Resolve the auto-answer (find which variant matches the score)
            const { variantId, variantText } = this.resolveAutoAnswer(autoQ, newScore);
            
            if (variantId !== null && variantText !== null) {
                // Save the auto-answer with the resolved variant FIRST
                this.$events.emit("answerChanged", new SingleItemAnswer(
                    autoQ.id,
                    [],
                    variantId,
                    [variantText]
                ));
                
                // NOW process value-dependent questions with the saved auto-answer
                const valueDependents = this.localQuestions.filter(q => 
                    q.questionConditionId === autoQ.id
                );
                
                valueDependents.forEach(depQ => {
                    // Clear the dependent question's answer
                    this.removeAnswer(depQ);
                    
                    // Check if the dependent should be shown/hidden based on the resolved variant
                    const variantStr = String(variantId);
                    const shouldShow = depQ.answerQuestions?.includes(variantStr) ? 
                        (depQ.conditionQuestion === 'in') : 
                        (depQ.conditionQuestion === 'notIn');
                    
                    if (!shouldShow) {
                        depQ.isHidden = true;
                        this.updateQuestionVisibilityInStore(depQ.id, true);
                        // Recursively hide this question's dependents
                        this.hideDepQuestions(depQ);
                    } else {
                        // For auto-answer questions, always keep them hidden from UI
                        if (depQ.autoAnswer === true) {
                            depQ.isHidden = true;
                            this.updateQuestionVisibilityInStore(depQ.id, true);
                        } else {
                            depQ.isHidden = false;
                            this.updateQuestionVisibilityInStore(depQ.id, false);
                        }
                        
                        // CRITICAL: Even though this question is shown, its answer was cleared
                        // So we need to hide/clear all of ITS dependents since they now have no valid condition to check
                        this.hideDepQuestions(depQ);
                    }
                });
            } else {
                // Could not resolve auto-answer, hide all dependents
                const valueDependents = this.localQuestions.filter(q => 
                    q.questionConditionId === autoQ.id
                );
                
                valueDependents.forEach(depQ => {
                    depQ.isHidden = true;
                    this.updateQuestionVisibilityInStore(depQ.id, true);
                    this.removeAnswer(depQ);
                    this.hideDepQuestions(depQ);
                });
            }
        } else {
            // If we can't calculate the score, hide all dependents
            const valueDependents = this.localQuestions.filter(q => 
                q.questionConditionId === autoQ.id
            );
            
            valueDependents.forEach(depQ => {
                depQ.isHidden = true;
                this.updateQuestionVisibilityInStore(depQ.id, true);
                this.removeAnswer(depQ);
                this.hideDepQuestions(depQ);
            });
        }
    });
}

  async confirm() {
    const dependentQuestions = [...this.localQuestions]
      .filter(p => this.dependentQuestionNames.includes(p.question));
    
    // Check if this is a range or yes/no question
    const currentQuestion = this.localQuestions.find(q => q.id === this.questionId);
    const isRangeQuestion = currentQuestion?.questionSubType === "HowMany - SelectOne Range";
    const isYesNoQuestion = currentQuestion?.questionSubType === "HowMany - YesNo";
    
    // Separate auto-answer questions from regular dependent questions
    const autoAnswerQuestions = dependentQuestions.filter(q => 
      q.autoAnswer === true && q.referenceQuestionId === this.questionId
    );
    
    const regularDependentQuestions = dependentQuestions.filter(q => 
      !(q.autoAnswer === true && q.referenceQuestionId === this.questionId)
    );
    
    // Process auto-answer cascade first (if any)
    if (autoAnswerQuestions.length > 0) {
      this.processAutoAnswerCascade(autoAnswerQuestions);
    }
    
    switch(this.type) {
      case 'single':
        // Handle range/yes-no questions specially
        if (isRangeQuestion || isYesNoQuestion) {
          // For range/yes-no questions, use totalScore for dependent logic
          const scoreToCheck = this.totalScore !== undefined ? String(this.totalScore) : String(this.variantId);
          
          for (let i = 0; i < regularDependentQuestions.length; i++) {
            this.removeAnswer(regularDependentQuestions[i]);

            if (regularDependentQuestions[i].questionConditionId === this.questionId) {
              const res = regularDependentQuestions[i].answerQuestions?.find(prop => prop === scoreToCheck);
              const shouldHide = Boolean((!res && regularDependentQuestions[i].conditionQuestion === 'in') ||
                                (res && regularDependentQuestions[i].conditionQuestion === 'notIn'));
              
              regularDependentQuestions[i].isHidden = shouldHide;
              this.updateQuestionVisibilityInStore(regularDependentQuestions[i].id, shouldHide);
              
              this.hideDepQuestions(regularDependentQuestions[i]);
            }
          }
          
          // Save the answer with total score and selections
          const selections = isRangeQuestion ? this.rangeSelections : this.yesNoSelections;
          this.$events.emit("answerChanged", new SingleItemAnswer(
            this.questionId, 
            [], 
            this.totalScore, 
            [JSON.stringify(selections)]
          ));
        } else {
          // Original logic for standard single questions
          for (let i = 0; i < regularDependentQuestions.length; i++) {
            this.removeAnswer(regularDependentQuestions[i]);

            if (regularDependentQuestions[i].questionConditionId === this.questionId) {
              const res = regularDependentQuestions[i].answerQuestions?.find(prop => String(prop) === String(this.variantId));
              const shouldHide = Boolean((!res && regularDependentQuestions[i].conditionQuestion === 'in') ||
                                (res && regularDependentQuestions[i].conditionQuestion === 'notIn'));
              
              regularDependentQuestions[i].isHidden = shouldHide;
              this.updateQuestionVisibilityInStore(regularDependentQuestions[i].id, shouldHide);
              
              this.hideDepQuestions(regularDependentQuestions[i]);
            }
          }
          
          this.$events.emit("answerChanged", new SingleItemAnswer(this.questionId, [], this.variantId, [this.variantText!]));
        }
        break;
        
      case 'multi':
        for (let i = 0; i < dependentQuestions.length; i++) {
          this.removeAnswer(dependentQuestions[i]);

          if (dependentQuestions[i].questionConditionId === this.questionId) {
            const res = dependentQuestions[i].answerQuestions?.filter(prop => this.variantsIds!.includes(Number(prop)));
            const shouldHide = Boolean(((res && res.length === 0 && dependentQuestions[i].conditionQuestion === 'in') ||
                              (res && res.length > 0 && dependentQuestions[i].conditionQuestion === 'notIn')));
            
            dependentQuestions[i].isHidden = shouldHide;
            this.updateQuestionVisibilityInStore(dependentQuestions[i].id, shouldHide);
            
            this.hideDepQuestions(dependentQuestions[i]);
          }
        }

        this.$events.emit("answerChanged", new MultipleItemsAnswer(this.questionId, [], this.variantsIds, this.variantsTexts));
        break;
        
      case 'bool':
        for (let i = 0; i < dependentQuestions.length; i++) {
          this.removeAnswer(dependentQuestions[i]);

          if (dependentQuestions[i].questionConditionId === this.questionId) {
            const res = dependentQuestions[i].answerQuestions?.find(prop => prop === String(this.booleanAnswer));
            const shouldHide = Boolean((!res && dependentQuestions[i].conditionQuestion === 'in') ||
                              (res && dependentQuestions[i].conditionQuestion === 'notIn'));
            
            dependentQuestions[i].isHidden = shouldHide;
            this.updateQuestionVisibilityInStore(dependentQuestions[i].id, shouldHide);
            
            this.hideDepQuestions(dependentQuestions[i]);
          }
        }

        this.$events.emit("answerChanged", new BooleanAnswer(this.questionId, this.booleanAnswer, this.booleanAnswer ? "Yes" : "No"));
        break;
    }
    
    this.hideModal();
  }
}
</script>

<style lang="scss" scoped>
.modal {
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: rgba(0, 0, 0, 0.5);
  position: fixed;
  display: flex;
}
  
.modal-content {
  margin: auto;
  background: white;
  border-radius: 12px;
  min-height: 183px;
  min-width: 300px;
  max-width: 435px;
  border: 1px solid #FFFFFF;
}

.m-header {
  display: flex;
  justify-content: space-between;
}

.m-header-text {
  font-size: 18px;
  font-weight: 600;
}

.close-modal-img {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.feedback-text {
  resize: none;
  max-width: 484px;
  height: 128px;
  border: 1px solid #E2E4EB;
  border-radius: 8px;
  padding: 10px;
  margin-top: 15px;
  margin-bottom: 10px;
  width: 100%;
}

.feedback-text:focus {
  outline: none;
  border: 1px solid #8D939E;
}

.additional-text {
  font-size: 14px;
  font-weight: 400;
}

.divider {
  height: 1px;
  max-width: 485px;
  background-color: #DAE0EA;
}

.actions-btns {
  display: flex;
  justify-content: end;
  margin-top: 15px;
}

.mt-15 {
  margin-top: 15px;
}

ul {
  margin: 0;
  padding: 0;
}

li {
  list-style: none;
}
</style>